'use client';

import { Button } from '@/components/ui/button';
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldLabel,
  FieldLegend,
  FieldSet,
  FieldTitle,
} from '@/components/ui/field';
import { Separator } from '@/components/ui/separator';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import {
  type UpdateOrganizationIntent,
  useActiveOrganization,
  useUpdateOrganization,
} from '@/features/organization';
import {
  updateOrganizationFields,
  updateOrganizationForm,
} from '@/features/organization/api/update-organization/update-organization.form';
import { ROUTES } from '@/lib/route-paths';
import { cn } from '@/lib/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

// Save only cancellation, rescheduling and deposit-forfeiture preferences.
const F = updateOrganizationFields;

const cancellationsSchema = z.object({
  customerReschedulingEnabled: F.customerReschedulingEnabled.schema,
  customerCancellationsEnabled: F.customerCancellationsEnabled.schema,
  cancellationRescheduleDeadlineHours:
    F.cancellationRescheduleDeadlineHours.schema,
  depositForfeitOnLateCancel: F.depositForfeitOnLateCancel.schema,
  depositForfeitOnNoShow: F.depositForfeitOnNoShow.schema,
});

type CancellationsFormValues = z.infer<typeof cancellationsSchema>;

export function CancellationsTab({
  className,
  onCancel,
  ...props
}: React.ComponentProps<'form'> & { onCancel?: () => void }) {
  const L = updateOrganizationForm.labels;
  const {
    data: orgData,
    isPending: isOrganizationLoading,
    error: organizationError,
  } = useActiveOrganization();

  const organizationData = orgData;
  const { execute: updateOrganization, isExecuting: isUpdating } =
    useUpdateOrganization();

  const form = useForm<CancellationsFormValues>({
    resolver: zodResolver(cancellationsSchema),
    defaultValues: {
      customerReschedulingEnabled:
        organizationData?.customerReschedulingEnabled ?? true,
      customerCancellationsEnabled:
        organizationData?.customerCancellationsEnabled ?? true,
      cancellationRescheduleDeadlineHours:
        organizationData?.cancellationRescheduleDeadlineHours != null
          ? Number(organizationData.cancellationRescheduleDeadlineHours)
          : 24,
      depositForfeitOnLateCancel:
        organizationData?.depositForfeitOnLateCancel ?? true,
      depositForfeitOnNoShow: organizationData?.depositForfeitOnNoShow ?? true,
    },
  });

  const takesDeposits =
    organizationData?.depositEnabled ||
    (organizationData?.defaultPaymentPolicy ?? 'in_clinic') !== 'in_clinic';

  useEffect(() => {
    if (organizationData) {
      form.reset({
        customerReschedulingEnabled:
          organizationData.customerReschedulingEnabled ?? true,
        customerCancellationsEnabled:
          organizationData.customerCancellationsEnabled ?? true,
        cancellationRescheduleDeadlineHours:
          organizationData.cancellationRescheduleDeadlineHours != null
            ? Number(organizationData.cancellationRescheduleDeadlineHours)
            : 24,
        depositForfeitOnLateCancel:
          organizationData.depositForfeitOnLateCancel ?? true,
        depositForfeitOnNoShow: organizationData.depositForfeitOnNoShow ?? true,
      });
    }
  }, [organizationData, form]);

  async function onSubmit(values: CancellationsFormValues) {
    const payload: UpdateOrganizationIntent = {};

    // Cancellation and rescheduling share a policy section and deadline,
    // but customers can be allowed to use either action independently.
    payload.customerReschedulingEnabled = values.customerReschedulingEnabled;
    payload.customerCancellationsEnabled = values.customerCancellationsEnabled;

    // The ONE deadline governing both online cancel and online reschedule
    // is always sent (it is a general policy, not gated
    // by either toggle above). The forfeit switches only matter where a
    // deposit is actually taken; sent regardless so a later switch to taking
    // deposits doesn't need this tab revisited.
    payload.cancellationRescheduleDeadlineHours =
      values.cancellationRescheduleDeadlineHours;
    payload.depositForfeitOnLateCancel = values.depositForfeitOnLateCancel;
    payload.depositForfeitOnNoShow = values.depositForfeitOnNoShow;

    await updateOrganization(payload);
  }

  if (isOrganizationLoading) {
    return (
      <div className={cn('flex flex-col px-6 py-4 gap-6', className)}>
        <div className="animate-pulse space-y-6">
          <div className="h-12 bg-muted rounded" />
          <div className="h-12 bg-muted rounded" />
          <div className="h-12 bg-muted rounded" />
        </div>
      </div>
    );
  }

  if (organizationError) {
    return (
      <div className={cn('flex flex-col px-6 py-4 gap-6', className)}>
        <div className="text-destructive">
          Error loading organization data: {organizationError.message}
        </div>
      </div>
    );
  }

  return (
    <form
      className={cn('flex flex-col px-6 py-4', className)}
      onSubmit={form.handleSubmit(onSubmit)}
      {...props}
    >
      {/* One policy section: independent online permissions, one shared
          deadline, and separate deposit outcomes for late cancels / no-shows. */}
      <FieldSet>
        <FieldLegend>Cancellation and rescheduling policy</FieldLegend>
        <FieldDescription className="-mt-2 mb-1">
          Choose which changes customers can make online, the shared deadline,
          and what happens to any deposit they have paid.
        </FieldDescription>

        <Controller
          name="customerReschedulingEnabled"
          control={form.control}
          render={({ field }) => (
            <Field orientation="horizontal">
              <FieldContent>
                <FieldTitle>{L.customerReschedulingEnabled}</FieldTitle>
                <FieldDescription>
                  Customers can reschedule their own bookings from the portal
                  before the deadline below. After it, they must contact you.
                </FieldDescription>
              </FieldContent>
              <Switch
                checked={field.value}
                onCheckedChange={field.onChange}
                aria-label={L.customerReschedulingEnabled}
              />
            </Field>
          )}
        />

        <Controller
          name="customerCancellationsEnabled"
          control={form.control}
          render={({ field }) => (
            <Field orientation="horizontal">
              <FieldContent>
                <FieldTitle>{L.customerCancellationsEnabled}</FieldTitle>
                <FieldDescription>
                  Customers can cancel their own bookings from the portal,
                  including after the deadline. Late cancellations follow the
                  deposit rule below.
                </FieldDescription>
              </FieldContent>
              <Switch
                checked={field.value}
                onCheckedChange={field.onChange}
                aria-label={L.customerCancellationsEnabled}
              />
            </Field>
          )}
        />
        <Controller
          name="cancellationRescheduleDeadlineHours"
          control={form.control}
          render={({ field }) => (
            <Field className="mt-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <FieldLabel htmlFor={field.name} className="font-medium">
                  {L.cancellationRescheduleDeadlineHours}
                </FieldLabel>
                <span className="text-muted-foreground text-sm">
                  {(field.value ?? 0) === 0
                    ? 'No deadline'
                    : `${field.value} hours' notice`}
                </span>
              </div>
              <FieldDescription>
                The same deadline applies to online rescheduling and late
                cancellations, including cancellations made by staff. No-shows
                follow their own deposit rule regardless of this deadline.
              </FieldDescription>
              <Slider
                id={field.name}
                value={[field.value ?? 0]}
                onValueChange={([hours]) => field.onChange(hours)}
                min={0}
                max={48}
                step={1}
                aria-label={L.cancellationRescheduleDeadlineHours}
              />
            </Field>
          )}
        />

        {takesDeposits ? (
          <>
            <Controller
              name="depositForfeitOnLateCancel"
              control={form.control}
              render={({ field }) => (
                <Field orientation="horizontal" className="mt-2">
                  <FieldContent>
                    <FieldTitle>{L.depositForfeitOnLateCancel}</FieldTitle>
                    <FieldDescription>
                      Keep the paid deposit when a customer or staff member
                      cancels after the deadline. Turn this off to refund it,
                      just like an early cancellation.
                    </FieldDescription>
                  </FieldContent>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    aria-label={L.depositForfeitOnLateCancel}
                  />
                </Field>
              )}
            />

            <Controller
              name="depositForfeitOnNoShow"
              control={form.control}
              render={({ field }) => (
                <Field orientation="horizontal" className="mt-2">
                  <FieldContent>
                    <FieldTitle>{L.depositForfeitOnNoShow}</FieldTitle>
                    <FieldDescription>
                      Keep the paid deposit when staff mark a booking as a
                      no-show. Turn this off to refund it.
                    </FieldDescription>
                  </FieldContent>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    aria-label={L.depositForfeitOnNoShow}
                  />
                </Field>
              )}
            />
          </>
        ) : (
          <FieldDescription className="mt-2">
            Bookings without an online deposit have nothing to forfeit. The
            deadline still applies to online rescheduling. Turn on{' '}
            <a href={ROUTES.settingsBookings} className="font-medium">
              {L.depositEnabled}
            </a>{' '}
            in Booking settings to set up deposits.
          </FieldDescription>
        )}
      </FieldSet>

      <Separator className="my-4" />

      <div className="flex gap-2">
        <Button type="submit" disabled={isUpdating}>
          {isUpdating ? 'Saving...' : 'Submit'}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
